/**
 * Session-Speicher (low-level): Token + Nutzer, persistiert in localStorage.
 *
 * Bewusst frei von API-Aufrufen, damit der API-Client hierauf zugreifen kann,
 * ohne einen Import-Zyklus zu erzeugen. High-level Auth-Logik (Login/Logout/
 * Bootstrap) liegt in src/state/auth.ts.
 *
 * Hinweis (Konzept §7): localStorage ist für die Test-PWA ok; iOS kann den
 * Storage verdrängen → erneutes Login. Mit Capacitor folgt Secure-Storage.
 */
import { signal, computed } from "@preact/signals";
import type { ApiUser } from "../api/types";

/**
 * Storage-Schlüssel 1:1 wie die Web-App (`ki-kurs-*`). So sind App und Website
 * bei gemeinsamer Domain session-/fortschritt-kompatibel und der gesamte
 * localStorage-Vertrag aus der Funktions-Analyse (Dok B §1) gilt unverändert.
 */
export const TOKEN_KEY = "ki-kurs-api-token";
export const USER_KEY = "ki-kurs-user";
export const LOGIN_FLAG_KEY = "ki-kurs-eingeloggt";
export const ROLE_KEY = "ki-kurs-user-role";
export const PROFILE_KEY = "ki-kurs-profile";
export const ACTIVE_KEY = "ki-kurs-aktiv";

/** Profil-ID wie im Web: `user:<email-lower>`. */
export function profileId(email: string): string {
  return "user:" + String(email || "local").toLowerCase();
}

/** Aktive Profil-ID (für profil-namensraumigen Fortschritt/letzte Position). */
export function activeProfileId(): string {
  const u = user.value;
  if (u?.email) return profileId(u.email);
  return localStorage.getItem(ACTIVE_KEY) || "local";
}

function readUser(): ApiUser | null {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? (JSON.parse(raw) as ApiUser) : null;
  } catch {
    return null;
  }
}

export const token = signal<string | null>(localStorage.getItem(TOKEN_KEY));
export const user = signal<ApiUser | null>(readUser());

export const isAuthenticated = computed(() => Boolean(token.value));
export const isTrainer = computed(() => {
  const role = (user.value?.role ?? "").toLowerCase();
  return ["admin", "trainer", "instructor", "teacher"].includes(role);
});

function persistUser(newUser: ApiUser): void {
  localStorage.setItem(USER_KEY, JSON.stringify(newUser));
  if (newUser.role) localStorage.setItem(ROLE_KEY, newUser.role);
  // Profil-Liste + aktives Profil wie im Web (storeUser), damit Fortschritt/
  // letzte Position pro Profil namensraumig sind.
  if (newUser.email) {
    const id = profileId(newUser.email);
    const name = newUser.display_name || newUser.preferred_name || newUser.email;
    localStorage.setItem(PROFILE_KEY, JSON.stringify([{ id, name, email: newUser.email }]));
    localStorage.setItem(ACTIVE_KEY, id);
  }
}

export function setSession(newToken: string, newUser: ApiUser): void {
  token.value = newToken;
  user.value = newUser;
  localStorage.setItem(TOKEN_KEY, newToken);
  localStorage.setItem(LOGIN_FLAG_KEY, "1");
  persistUser(newUser);
}

export function setUser(newUser: ApiUser): void {
  user.value = newUser;
  persistUser(newUser);
}

export function clearSession(): void {
  token.value = null;
  user.value = null;
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(LOGIN_FLAG_KEY);
  localStorage.removeItem(ROLE_KEY);
}

export function getToken(): string | null {
  return token.value;
}
